import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { NotificationService } from "../../services/NotificationService";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import { CustomTableRowTR, StyledTable, TableData, TableHeader } from "../../styles/commonStyles";
import CircularLoader from "./CircularLoader";
import EmptyListPlaceholder from "./EmptyListPlaceholder";

const notificationService = new NotificationService();
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const CHANNELS = ["FCM", "WHATSAPP", "SMS"];
const REFERENCE_MODEL_NAMES = ["BOOK_BY_DISH", "CHEF_FOR_PARTY", "ONE_MEAL_SERVICE"];
const USER_TYPE_OPTIONS = [
  { label: "Customer App", value: "CUSTOMER" },
  { label: "Partner App", value: "COOK" },
];

const extractMessage = (item: any): string => {
  const { channel, message } = item;
  if (channel === "WHATSAPP") {
    return message?.renderedText || "---";
  }
  if (channel === "SMS") {
    return typeof message?.body === "string" ? message.body : "---";
  }
  if (channel === "FCM") {
    const title = message?.body?.notification?.title || "";
    const body = message?.body?.notification?.body || "";
    if (title && body) return `${title}\n${body}`;
    return body || title || "---";
  }
  return "---";
};

const statusColor = (
  status: string
): "success" | "warning" | "error" | "default" => {
  const s = (status || "").toUpperCase();
  if (s === "SUCCESS" || s === "SENT") return "success";
  if (s === "PENDING") return "warning";
  if (s === "FAILED") return "error";
  return "default";
};

interface Props {
  referenceId?: number;
  referenceModelName?: string;
  userId?: number;
  userType?: string;
}

const ServiceNotificationsTab = ({
  referenceId,
  referenceModelName,
  userId,
  userType,
}: Props) => {
  const [data, setData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based for TablePagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [channel, setChannel] = useState("");
  const [filterRefModelName, setFilterRefModelName] = useState("");
  const [filterRefId, setFilterRefId] = useState("");
  const [filterUserType, setFilterUserType] = useState("");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  const fetchData = useCallback(
    (currentPage: number, currentRowsPerPage: number) => {
      setIsLoading(true);
      const params: any = {
        limit: currentRowsPerPage,
        offset: currentPage * currentRowsPerPage,
        sortField: "id",
        sortOrder,
      };
      if (userId) {
        params.userId = userId;
        params.userType = userType;
      }
      if (referenceId) params.referenceId = referenceId;
      if (referenceModelName) params.referenceModelName = referenceModelName;
      if (!referenceId && filterRefModelName)
        params.referenceModelName = filterRefModelName;
      if (!referenceId && filterRefId)
        params.referenceId = Number(filterRefId);
      if (channel) params.channel = channel;
      if (filterUserType) params.userType = filterUserType;

      notificationService
        .getNotifications(params)
        .then((res: any) => {
          setData(res?.data?.data || res?.data || []);
          setCount(res?.data?.count || 0);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    },
    [referenceId, referenceModelName, userId, userType, channel, filterRefModelName, filterRefId, filterUserType, sortOrder]
  );

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [fetchData, page, rowsPerPage]);

  const handleChannelChange = (val: string) => {
    setChannel(val);
    setPage(0);
  };

  const handleIdSort = () => {
    setSortOrder((prev) => (prev === "DESC" ? "ASC" : "DESC"));
    setPage(0);
  };

  const handleClear = () => {
    setChannel("");
    setFilterRefModelName("");
    setFilterRefId("");
    setFilterUserType("");
    setPage(0);
  };

  return (
    <Box p={2}>
      {/* Filter Section */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "8px",
          backgroundColor: "rgba(232, 235, 237, 0.2)",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1.5, fontWeight: 600, fontSize: "13px", color: "#374151" }}
        >
          Filters
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Channel</InputLabel>
            <Select
              value={channel}
              label="Channel"
              onChange={(e) => handleChannelChange(e.target.value)}
              sx={{ fontSize: "13px" }}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
              {CHANNELS.map((c) => (
                <MenuItem key={c} value={c} sx={{ fontSize: "13px" }}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: "13px" }}>App Type</InputLabel>
            <Select
              value={filterUserType}
              label="App Type"
              onChange={(e) => {
                setFilterUserType(e.target.value);
                setPage(0);
              }}
              sx={{ fontSize: "13px" }}
            >
              <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
              {USER_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!referenceId && (
            <>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel sx={{ fontSize: "13px" }}>Reference Model</InputLabel>
                <Select
                  value={filterRefModelName}
                  label="Reference Model"
                  onChange={(e) => {
                    setFilterRefModelName(e.target.value);
                    setPage(0);
                  }}
                  sx={{ fontSize: "13px" }}
                >
                  <MenuItem value="" sx={{ fontSize: "13px" }}>All</MenuItem>
                  {REFERENCE_MODEL_NAMES.map((m) => (
                    <MenuItem key={m} value={m} sx={{ fontSize: "13px" }}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Reference ID"
                value={filterRefId}
                onChange={(e) => {
                  setFilterRefId(e.target.value);
                  setPage(0);
                }}
                type="number"
                inputProps={{ style: { fontSize: "13px" } }}
                InputLabelProps={{ style: { fontSize: "13px" } }}
                sx={{ minWidth: 140 }}
              />
            </>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={handleClear}
            sx={{ fontSize: "13px", textTransform: "none", height: "40px" }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {isLoading && <CircularLoader />}

      {!isLoading && data.length === 0 && (
        <EmptyListPlaceholder message="No Notifications Found" />
      )}

      {data.length > 0 && (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: "8px", overflowX: "auto" }}>
            <StyledTable
              size="small"
              sx={{ minWidth: 900, tableLayout: "fixed", width: "100%" }}
            >
              <colgroup>
                <col style={{ width: "70px" }} />
                <col style={{ width: "155px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "130px" }} />
                <col />
                <col style={{ width: "110px" }} />
                <col style={{ width: "185px" }} />
              </colgroup>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                  <TableHeader>
                    <TableSortLabel
                      active
                      direction={sortOrder === "DESC" ? "desc" : "asc"}
                      onClick={handleIdSort}
                    >
                      ID
                    </TableSortLabel>
                  </TableHeader>
                  <TableHeader>Reference</TableHeader>
                  <TableHeader>App Type</TableHeader>
                  <TableHeader>Channel</TableHeader>
                  <TableHeader>Message</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Date &amp; Time</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item: any) => (
                  <CustomTableRowTR key={item.id}>
                    <TableData sx={{ fontSize: "13px", whiteSpace: "nowrap" }}>
                      {item.id}
                    </TableData>
                    <TableData sx={{ fontSize: "12px" }}>
                      <Box>
                        <div style={{ fontWeight: 500 }}>{item.referenceModelName || "---"}</div>
                        <div style={{ color: "#6b7280" }}>#{item.referenceId}</div>
                      </Box>
                    </TableData>
                    <TableData sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={item.userType === "CUSTOMER" ? "CX App" : item.userType === "COOK" ? "Partner App" : item.userType || "---"}
                        size="small"
                        variant="outlined"
                        color={item.userType === "CUSTOMER" ? "primary" : item.userType === "COOK" ? "success" : "default"}
                        sx={{ maxWidth: "none", "& .MuiChip-label": { overflow: "visible", whiteSpace: "nowrap" } }}
                      />
                    </TableData>
                    <TableData sx={{ whiteSpace: "nowrap" }}>
                      <Chip label={item.channel} size="small" variant="outlined" />
                    </TableData>
                    <TableData>
                      <Box
                        sx={{
                          fontSize: "13px",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.6,
                        }}
                      >
                        {extractMessage(item)}
                      </Box>
                    </TableData>
                    <TableData sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={item.status}
                        size="small"
                        color={statusColor(item.status)}
                      />
                    </TableData>
                    <TableData sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                      {item.createdAt ? formatDateTimeWithSeconds(item.createdAt) : "---"}
                    </TableData>
                  </CustomTableRowTR>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>

          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            sx={{
              "& .MuiTablePagination-toolbar": { alignItems: "center" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { margin: 0 },
            }}
          />
        </>
      )}
    </Box>
  );
};

export default ServiceNotificationsTab;
